import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { unlockMhCalendar } from "@/lib/mh-gate.functions";
import portrait from "@/assets/mh-portrait.jpg.asset.json";

const STORAGE_KEY = "fc-mh-calendar";

export const Route = createFileRoute("/mh")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Appointment booking — Foreign Council" },
      { name: "description", content: "Private appointment booking." },
      { property: "og:title", content: "Appointment booking — Foreign Council" },
      { property: "og:description", content: "Private appointment booking." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow, noarchive" },
    ],
  }),
  component: MhPage,
});

function MhPage() {
  const unlock = useServerFn(unlockMhCalendar);
  const [url, setUrl] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved?.startsWith("https://calendly.com/")) setUrl(saved);
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const password = String(new FormData(e.currentTarget).get("password") ?? "");
    setBusy(true);
    setError(false);
    try {
      const res = await unlock({ data: { password } });
      if (res.ok) {
        sessionStorage.setItem(STORAGE_KEY, res.url);
        setUrl(res.url);
      } else setError(true);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  const embed = url ? `${url}?hide_gdpr_banner=1&primary_color=c8102e` : null;

  return (
    <section className="bg-surface">
      <div className="mx-auto w-full max-w-[1100px] px-5 py-24 lg:px-8 lg:py-28">
        <p className="fc-h4 fc-rule text-muted-foreground">Appointment booking</p>
        <div className="mt-6 flex items-center gap-5">
          {embed && (
            <img
              src={portrait.url}
              alt="Maximilian Hoffmann"
              width={96}
              height={96}
              className="h-24 w-24 shrink-0 rounded-full object-cover"
            />
          )}
          <div>
            <h1 className="fc-h2 font-bold text-foreground">Maximilian Hoffmann</h1>
            <p className="mt-1 fc-h4 text-foreground">President</p>
          </div>
        </div>

        {embed ? (
          <div className="mt-10">
            <p className="mb-4 fc-body text-muted-foreground">Please be patient — the calendar may take a few moments to load.</p>
            <iframe
              src={embed}
              title="Calendar – book an appointment"
              className="h-[1100px] w-full border border-border bg-card md:h-[760px]"
              loading="lazy"
            />
            <Button asChild variant="link" className="mt-4 h-auto p-0 fc-body">
              <a href={url!} target="_blank" rel="noopener noreferrer">Open calendar in a new window</a>
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-10 max-w-md space-y-4 border border-dashed border-foreground bg-card p-6">
            <label htmlFor="mh-password" className="block fc-body font-bold text-foreground">Password</label>
            <div className="flex gap-2">
              <input
                id="mh-password"
                name="password"
                type={show ? "text" : "password"}
                autoComplete="current-password"
                required
                aria-invalid={error}
                aria-describedby={error ? "mh-error" : undefined}
                className="w-full border border-input bg-background px-3 py-2 fc-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-pressed={show}
                aria-label={show ? "Hide password" : "Show password"}
                onClick={() => setShow((s) => !s)}
              >
                {show ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
              </Button>
            </div>
            {error && <p id="mh-error" role="alert" className="fc-body text-destructive">Incorrect password.</p>}
            <Button type="submit" disabled={busy}>Open calendar</Button>
          </form>
        )}
      </div>
    </section>
  );
}
